///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subject, Subscription } from 'rxjs';
import { GtthAlertService } from '../../../services/alert.service';
import { ProjectService } from '../../../services/project/project.service';
import { GtthEventManager } from '../../../services/event-manager.service';
import { GtthEntityNameValidation } from '../../../shared/directives/validation-functions/entity-name';
import { parseFormDataToObj } from '../../../shared/utils';
import {
    EngineProcessTypeEnum,
    ExtendedProductdata,
    PlannerFeatureCollectionDTO,
    ProjectDTO,
    ProjectFolderEnum
} from '../../../generated/shared-types.model';
import { PARAM_CUSTOMER_ID, PARAM_TREE_ID } from '../../../shared/constants/url-params.constants';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { GtthUpdateMainConfigCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/update-main-config-command';
import { GtthProjectStateService } from '../../../project-view-panel/logical-topology/engine/project-state.service';
import { GtthHistoryService } from '../../../project-view-panel/logical-topology/engine/history-service';
import { GtthRefreshTopologyCommand } from '../../../project-view-panel/logical-topology/engine/commands/project/refresh-topology-command';
import { ProjectProductsService } from '../../../project-view-panel/logical-topology/engine/project-products-service';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { Store } from '@ngrx/store';
import { createProject } from '../store/project-modal-management/project-modal-management.actions';
import { ProjectDialogProductsService } from '../project-dialog/project-dialog-products.service';
import { LogicalConfigModel } from '../../../services/logical-topology/logical-config.model';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { GtthLayerDtoModel } from '../../../services/project-socket/dto/gtth-layer-dto.model';
import { ProjectDialogComponent } from '../project-dialog/project-dialog.component';
import { isDefined } from '@siklu/sui-lib/utils';
import { SuiCheckboxControlComponent, SuiLoadingDirective, SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { GtthSharedCommonModule } from '../../../shared/shared-common.module';
import { GtthBOMProductsSelectComponent } from './bom-products-select/bom-products-select.component';
import { GtthDisableFormControlsDirective } from '../../../shared/directives/validation-directives/disable-form-controls.directive';

@Component({
    selector: 'winde-bom-configurator-dialog',
    templateUrl: './bom-config-dialog.component.html',
    styleUrls: ['../../projects.scss'],
    providers: [ProjectDialogProductsService],
    imports: [
        FormsModule,
        MatDialogModule,
        SuiTranslatePipe,
        SuiSvgIconComponent,
        SuiLoadingDirective,
        GtthSharedCommonModule,
        GtthBOMProductsSelectComponent,
        SuiCheckboxControlComponent,
        GtthDisableFormControlsDirective
    ]
})
export class GtthCreateBOMConfiguratorDialogComponent implements OnInit, OnDestroy {
    @ViewChild(GtthBOMProductsSelectComponent) bomProductsSelectComponent: GtthBOMProductsSelectComponent;

    public editForm: FormGroup;
    public isLoading: boolean;
    public shouldOpenProject = true;
    public project: ProjectDTO;
    public isSaving: boolean;
    public showFormValidationAlert: boolean;
    public alertMessageSubject: Subject<string>;
    public logicalConfig: LogicalConfigModel;
    public productsIsValid = true;
    public defaultLayer: number = GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX;
    public viewInReadOnly = false;
    public loaderKey = LoaderIds.BOM_CONFIG_DIALOG_PRELOADER_ID;
    private newProjectId: number;
    private changeUnitsSubscriber: Subscription;

    constructor(
        private readonly store: Store,
        private readonly alertService: GtthAlertService,
        private readonly projectService: ProjectService,
        private readonly projectInfoService: GtthProjectInfoViewService,
        private readonly eventManager: GtthEventManager,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly formBuilder: FormBuilder,
        private readonly projectStateService: GtthProjectStateService,
        private readonly historyService: GtthHistoryService,
        private readonly projectProductsService: ProjectProductsService,
        public readonly dialogProductsService: ProjectDialogProductsService,
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<GtthCreateBOMConfiguratorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { project: ProjectDTO }
    ) {}

    ngOnInit(): void {
        this.preloaderService.show(this.loaderKey);
        this.isLoading = true;
        this.isSaving = false;
        this.project = this.data.project;
        if (isDefined(this.project.id)) {
            this.projectService.canBeModified(this.project.id).subscribe((projectIsNotLocked: boolean) => {
                this.viewInReadOnly = !projectIsNotLocked;
            });

            this.projectService.temp_getProjectLogicConfig(this.project.id).subscribe((geoJson: PlannerFeatureCollectionDTO) => {
                this.logicalConfig = geoJson as LogicalConfigModel;

                this.initForm();

                this.dialogProductsService
                    .loadRadioAndAntennaAndMhProducts(this.project, this.logicalConfig)
                    .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                    .subscribe(() => {
                        this.isLoading = false;
                    });
            });
        } else {
            this.logicalConfig = LogicalConfigModel.createDefault(false);
            this.logicalConfig.crs.properties.layers = [
                new GtthLayerDtoModel(this.defaultLayer, ProjectDialogComponent.DEFAULT_LAYER_NAMES[this.defaultLayer])
            ];
            this.logicalConfig.crs.properties.isShowPlanningMode = true;
            this.initForm();

            this.dialogProductsService
                .loadRadioAndAntennaAndMhProducts(this.project, this.logicalConfig)
                .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
                .subscribe(() => {
                    this.isLoading = false;
                });
        }
    }

    ngOnDestroy(): void {
        if (this.alertMessageSubject) {
            this.alertMessageSubject.complete();
        }
        if (this.changeUnitsSubscriber) {
            this.changeUnitsSubscriber.unsubscribe();
            this.changeUnitsSubscriber = undefined;
        }
    }

    save() {
        if (this.isSaving) {
            return;
        }

        parseFormDataToObj(this.project, this.editForm);

        this.bomProductsSelectComponent.updateLogicalTopologyWithSelectedProducts();

        const selectedProducts = this.logicalConfig.crs.properties.selectedProductsPerLayer[this.defaultLayer];
        this.sortSelectedProducts(selectedProducts);

        if (!this.productsIsValid) {
            this.alertService.showAlert('error.validation.field.bomLinkName.emptyOrDuplicatedName');
            return;
        }
        if (this.editForm.invalid) {
            this.alertService.showAlert('error.validation.form.required');
            return;
        }
        if (!selectedProducts?.length) {
            this.alertService.showAlert('error.validation.form.noProductsSelected');
            return;
        }

        this.preloaderService.show(this.loaderKey);
        this.isSaving = true;

        if (isDefined(this.project.id)) {
            this.projectService.update(this.project).subscribe({
                next: (res: ProjectDTO) => {
                    if (res) {
                        this.updateProjectConfig(res);
                    } else {
                        this.isSaving = false;
                        this.preloaderService.hide(this.loaderKey);
                    }
                },
                error: (res: Response) => this.onSaveError(res)
            });
        } else {
            this.projectService.create(this.project, 0, 0, 1).subscribe({
                next: (res: ProjectDTO) => {
                    if (res) {
                        this.newProjectId = res.id;
                        this.updateProjectConfig(res);
                        this.store.dispatch(createProject());
                    } else {
                        this.isSaving = false;
                        this.preloaderService.hide(this.loaderKey);
                    }
                },
                error: (res: Response) => this.onSaveError(res)
            });
        }
    }

    private sortSelectedProducts(arr: ExtendedProductdata[]) {
        if (!arr?.length) {
            return;
        }

        arr.sort((prod1: ExtendedProductdata, prod2: ExtendedProductdata) => {
            const prod1HasUsedProd = isDefined(prod1.usedProd);
            const prod2HasUsedProd = isDefined(prod2.usedProd);

            if (!prod2HasUsedProd) {
                return -1;
            }
            if (!prod1HasUsedProd) {
                return 1;
            }
            if (!prod1HasUsedProd && !prod2HasUsedProd) {
                return 0;
            }
            return 0;
        });

        for (let i = 0; i < arr.length; i++) {
            const product = arr[i];
            if (product.usedProd && !product.threshold) {
                product.threshold = i + 1;
            }
        }
    }

    private initForm() {
        this.alertMessageSubject = new Subject();
        this.editForm = this.formBuilder.group({
            name: [
                this.project.name,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(250),
                    GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
                ])
            ],
            description: [
                this.project.description,
                Validators.compose([Validators.maxLength(250), GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)])
            ],
            productUnits: this.logicalConfig.crs.properties.productUnits
        });

        this.changeUnitsSubscriber = this.editForm.get('productUnits').valueChanges.subscribe((val) => {
            this.logicalConfig.crs.properties.productUnits = val;
        });
    }

    private updateProjectConfig(newProject: ProjectDTO) {
        this.projectInfoService.setProjectName(newProject.name);

        if (this.projectStateService.projectIsOpened() && this.projectStateService.getProjectId() === newProject.id) {
            this.historyService.logicalTopology.selectedProducts = this.logicalConfig.crs.properties.selectedProductsPerLayer;
            this.historyService.logicalTopology.productUnits = this.logicalConfig.crs.properties.productUnits;

            const updateMainConfigCommand = this.historyService.command(GtthUpdateMainConfigCommand);
            this.historyService
                .doCommandBypassHistory(updateMainConfigCommand)
                .then(() => this.historyService.bomReportService.clearBom())
                .then(() => this.projectProductsService.loadProducts(newProject.id, true))
                .then(() => {
                    const refreshTopologyCommand = this.historyService.command(GtthRefreshTopologyCommand).createWithCacheInvalidation();
                    return this.historyService.doCommandBypassHistory(refreshTopologyCommand);
                })
                .then(() => this.onSaveSuccess())
                .catch((error: Error) => this.onSaveError(error));
        } else {
            this.projectService.temp_updateProjectMainConfig(newProject.id, this.logicalConfig).subscribe({
                next: () => {
                    this.onSaveSuccess();
                },
                error: (res: Response) => this.onSaveError(res)
            });
        }
    }

    private onSaveSuccess() {
        this.eventManager.broadcast({ name: 'projectModified', content: 'OK' });
        this.preloaderService.hide(this.loaderKey);

        this.isSaving = false;
        this.dialogRef.close();
        const projectsFolderId = -1;
        if (isDefined(this.newProjectId) && this.shouldOpenProject) {
            const params = { [PARAM_TREE_ID]: this.project.parentId };
            setTimeout(() => {
                this.router.navigate(['/project-view', this.newProjectId], { queryParams: params });
            }, 0);
        } else if (this.activatedRoute.snapshot.queryParams[PARAM_TREE_ID] === projectsFolderId) {
            const params = {};
            params[PARAM_TREE_ID] = this.project.parentId;
            params[PARAM_CUSTOMER_ID] = this.project.customerId;
            setTimeout(() => {
                this.router.navigate([], { queryParams: params, relativeTo: this.activatedRoute });
            }, 0);
        }
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
            console.log(`Failed to save: ${exception}`);
        }
        this.preloaderService.hide(this.loaderKey);
        this.isSaving = false;
        this.onError(error);
    }

    private onError(error) {
        this.alertService.error(error.message, null, null);
    }
}

@Component({
    selector: 'winde-bom-popup',
    template: ''
})
export class GtthCreateBOMConfiguratorPopupComponent implements OnInit {
    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly projectService: ProjectService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectService.getProject(id).subscribe((project: ProjectDTO) => {
                if (!project) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(GtthCreateBOMConfiguratorDialogComponent, {
                        width: '1200px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            project
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            const rootFolderIds = this.route.snapshot.data.rootFolderIds;
            const customerId = params.has('customerId') ? parseInt(params.get('customerId'), 10) : undefined;
            const parentId = params.has('parentId') ? parseInt(params.get('parentId'), 10) : rootFolderIds[ProjectFolderEnum.BOM_CONFIG];

            const project = {} as ProjectDTO;
            project.customerId = customerId;
            project.parentId = parentId;
            project.engineType = EngineProcessTypeEnum.BOM_CONFIGURATOR;

            const dialogRef = this.dialog.open(GtthCreateBOMConfiguratorDialogComponent, {
                width: '1200px',
                position: {
                    top: '30px'
                },
                data: {
                    project
                },
                closeOnNavigation: true
            });

            dialogRef.afterClosed().subscribe(() => {
                this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
            });
        }
    }
}
